import { ReactNode, useState } from 'react';
import classNames from 'classnames';
import { appendQueryParams } from '@bpm-web-app/utils';
import { useImmersiveHeader } from '../../../supreme-header/use-immersive-header';
import { DetailDescription } from './detail-description';
import { DetailMeta, MetaPillModel } from './detail-meta';
import styles from './detail-hero.module.css';

/* The image CDN has covers cached at this width (and 2x); any other width is resized on first request, ~1s. */
export const coverImageUrl = (url: string, density: 1 | 2 = 1) => appendQueryParams(url, { key: 'dw', value: 296 * density });

export interface DetailHeroProps {
    title: ReactNode;
    cover: ReactNode;
    backdropUrl?: string;
    byline?: ReactNode;
    description?: string;
    pills: MetaPillModel[];
    primaryActions?: ReactNode;
    secondaryActions?: ReactNode;
}

/** The cover blurred behind the hero, faded in once loaded so it doesn't pop in. */
function DetailBackdrop({ url }: { url: string }) {
    const [isLoaded, setIsLoaded] = useState(false);

    return (
        <div className={classNames(styles['detail-hero__backdrop'], { [styles['detail-hero__backdrop--loaded']]: isLoaded })} aria-hidden="true">
            <img className={styles['detail-hero__backdrop-art']} src={coverImageUrl(url)} alt="" onLoad={() => setIsLoaded(true)} />
        </div>
    );
}

/** Playlist-style detail header, with the cover blurred behind it under a transparent page header. */
export function DetailHero({ title, cover, backdropUrl, byline, description, pills, primaryActions, secondaryActions }: DetailHeroProps) {
    useImmersiveHeader();

    return (
        <div className={classNames(styles['detail-hero'], 'translucent-text-grays', 'translucent-surface-grays')}>
            {/* Keyed, so moving to another detail page fades the new cover in too. */}
            {backdropUrl && <DetailBackdrop key={backdropUrl} url={backdropUrl} />}
            <div className={styles['detail-hero__cover']}>{cover}</div>
            <div className={styles['detail-hero__text']}>
                <div className={styles['detail-hero__title-container']}>
                    <h2>{title}</h2>
                </div>
                {byline && <div className={styles['detail-hero__byline']}>{byline}</div>}
                {description && <DetailDescription key={description} text={description} />}
                <DetailMeta className={styles['detail-hero__meta']} pills={pills} />
            </div>
            <div className={styles['detail-hero__actions']}>
                {primaryActions && <div className={styles['detail-hero__primary']}>{primaryActions}</div>}
                {secondaryActions && <div className={styles['detail-hero__secondary']}>{secondaryActions}</div>}
            </div>
        </div>
    );
}

export default DetailHero;
